import {
  type UseInfiniteQueryResult,
  type UseQueryResult,
  useInfiniteQuery,
  useQuery,
} from '@tanstack/react-query';

import {
  type FetchInvoicesParams,
  INVOICE_TERMINAL_STATUSES,
  type InvoiceDetail,
  type InvoiceListItem,
  type InvoiceListPage,
  type InvoiceSummary,
  extractCursorFromUrl,
  fetchInvoice,
  fetchInvoiceSummary,
  fetchInvoices,
} from '~/api/invoices';
import { type InvoiceListFilters, queryKeys } from '~/api/queryKeys';

const POLL_INTERVAL_MS = 2_000;

interface InvoiceListData {
  results: InvoiceListItem[];
}

export type InfiniteInvoicesFilters = InvoiceListFilters;

/**
 * Consultas escopadas por empresa: `enabled` desliga a busca quando o usuário não tem
 * nível de consulta em NFS-e na empresa em operação. Sem isso o backend responderia
 * 403 e, com `throwOnError` global, a tela cairia no error boundary.
 */
export interface ScopedQueryOptions {
  enabled?: boolean;
}

function withClient(
  params: FetchInvoicesParams,
  clientId: number | undefined,
): FetchInvoicesParams {
  return clientId === undefined ? params : { ...params, client: clientId };
}

/**
 * Polling adaptativo: refetch a cada 2s enquanto existir nota em status
 * transitório; para quando tudo está em estado final. Backend responde
 * com cache Redis de 1s e throttle por usuário (60/min).
 */
export function useInvoicesQuery(
  clientId: number | undefined,
  params: FetchInvoicesParams = {},
  options: ScopedQueryOptions = {},
): UseQueryResult<InvoiceListData> {
  return useQuery({
    queryKey: queryKeys.invoices.list(clientId ?? null, params),
    queryFn: async () => {
      const data = await fetchInvoices(withClient(params, clientId));
      return { results: data.results };
    },
    enabled: options.enabled ?? true,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return false;
      const hasTransient = data.results.some(
        (invoice) => !INVOICE_TERMINAL_STATUSES.includes(invoice.status),
      );
      return hasTransient ? POLL_INTERVAL_MS : false;
    },
  });
}

type InvoicesPage = InvoiceListPage;

/**
 * Infinite scroll com cursor pagination. O cursor é extraído do `next`
 * devolvido pelo DRF; quando `next` é null, não há mais páginas. Polling
 * só acontece se alguma nota carregada estiver em status transitório,
 * limitado à primeira página (refetch nas outras seria caro e raramente
 * muda algo relevante na cauda da timeline).
 */
export function useInfiniteInvoicesQuery(
  clientId: number | undefined,
  filters: InfiniteInvoicesFilters = {},
  options: ScopedQueryOptions = {},
): UseInfiniteQueryResult<{ pages: InvoicesPage[]; pageParams: (string | null)[] }, Error> {
  return useInfiniteQuery({
    queryKey: queryKeys.invoices.infinite(clientId ?? null, filters),
    queryFn: ({ pageParam }) => {
      const params: FetchInvoicesParams = {};
      if (filters.status) params.status = filters.status;
      if (filters.mesAno) params.mes_ano = filters.mesAno;
      if (pageParam) params.cursor = pageParam;
      return fetchInvoices(withClient(params, clientId));
    },
    enabled: options.enabled ?? true,
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => extractCursorFromUrl(lastPage.next),
    refetchInterval: (query) => {
      const firstPage = query.state.data?.pages[0];
      if (!firstPage) return false;
      const hasTransient = firstPage.results.some(
        (invoice) => !INVOICE_TERMINAL_STATUSES.includes(invoice.status),
      );
      return hasTransient ? POLL_INTERVAL_MS : false;
    },
  });
}

export function useInvoiceQuery(id: string | undefined): UseQueryResult<InvoiceDetail> {
  return useQuery({
    queryKey: id ? queryKeys.invoices.detail(id) : ['invoices', 'detail', null],
    queryFn: () => {
      if (!id) throw new Error('id obrigatório');
      return fetchInvoice(id);
    },
    enabled: Boolean(id),
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return false;
      return INVOICE_TERMINAL_STATUSES.includes(data.status) ? false : POLL_INTERVAL_MS;
    },
  });
}

export function useInvoiceSummaryQuery(
  clientId: number | undefined,
  options: ScopedQueryOptions = {},
): UseQueryResult<InvoiceSummary> {
  return useQuery({
    queryKey: queryKeys.invoices.summary(clientId ?? null),
    queryFn: () => fetchInvoiceSummary(clientId),
    enabled: options.enabled ?? true,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return false;
      return data.count_processing > 0 ? POLL_INTERVAL_MS : false;
    },
    staleTime: 1_000,
  });
}
